import { API_URL } from '../config';

// Only these iframe origins may become embed sources. Anything else is
// rejected at parse time (pasted HTML) and at insert time (typed URLs),
// so stored article HTML can never carry an arbitrary iframe.
const RUMBLE_EMBED_PATTERN = /^https:\/\/rumble\.com\/embed\/[A-Za-z0-9.-]+(?:[/?#]|$)/;
const ALLOWED_EMBED_PATTERNS = [
    /^https:\/\/(?:www\.)?youtube\.com\/embed\/[A-Za-z0-9_-]{11}(?:[/?#]|$)/,
    /^https:\/\/(?:www\.)?youtube-nocookie\.com\/embed\/[A-Za-z0-9_-]{11}(?:[/?#]|$)/,
    RUMBLE_EMBED_PATTERN,
];

export const isAllowedEmbedSrc = (src) => (
    typeof src === 'string'
    && ALLOWED_EMBED_PATTERNS.some((pattern) => pattern.test(src.trim()))
);

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

const YOUTUBE_EMBED_SRC = /^https:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{11})(?:[/?#]|$)/i;

// The video id in a YouTube embed src (either host), else null.
// "videoseries" fits the id shape but is YouTube's playlist player, which
// has no single video (and no thumbnail).
export const getYouTubeEmbedId = (src) => {
    const match = YOUTUBE_EMBED_SRC.exec(typeof src === 'string' ? src.trim() : '');
    return match && match[1] !== 'videoseries' ? match[1] : null;
};

export const isRumbleEmbedSrc = (src) => (
    typeof src === 'string' && RUMBLE_EMBED_PATTERN.test(src.trim())
);

// An https URL with nothing that could break out of an attribute. The gate
// for third-party URLs (oEmbed thumbnails, video credit links).
export const isHttpsUrl = (value) => typeof value === 'string' && /^https:\/\/[^\s"'<>]+$/i.test(value.trim());

// An embed poster (an iframe's data-poster) is shown by the reader's browser
// in place of a third-party player, so it must be https — or a
// server-relative path / API URL, which media.js resolves against the
// (https) API origin. Shared by the editor (richTextFigure) and the public
// click-to-play facades (embedFacades).
export const isSafeEmbedPoster = (value) => {
    if (typeof value !== 'string' || !value.trim()) {
        return false;
    }
    const trimmed = value.trim();
    const apiBase = API_URL.replace(/\/+$/, '');
    return /^https:\/\//i.test(trimmed)
        || /^\/(?!\/)/.test(trimmed)
        || (Boolean(apiBase) && trimmed.startsWith(`${apiBase}/`));
};

// "1h2m3s", "2m10s", "90s", or plain "90" → seconds (0 when unparsable).
const parseTimeToSeconds = (value) => {
    if (!value) {
        return 0;
    }
    if (/^\d+$/.test(value)) {
        return Number.parseInt(value, 10);
    }
    const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(value);
    if (!match || (!match[1] && !match[2] && !match[3])) {
        return 0;
    }
    return (Number.parseInt(match[1] || '0', 10) * 3600)
        + (Number.parseInt(match[2] || '0', 10) * 60)
        + Number.parseInt(match[3] || '0', 10);
};

const youTubeEmbedUrl = (id, startSeconds) => {
    // Privacy-enhanced domain: no viewing cookies until playback starts.
    const base = `https://www.youtube-nocookie.com/embed/${id}`;
    return startSeconds > 0 ? `${base}?start=${startSeconds}` : base;
};

// Classifies a pasted/typed link:
//  - { type: 'embed', src }  — ready-to-use iframe URL
//  - { type: 'resolve', url } — a Rumble page link; the embed id differs
//    from the page id, so the backend must look it up via Rumble's oEmbed
//  - null — not a supported video link
export const parseEmbedInput = (text) => {
    if (typeof text !== 'string') {
        return null;
    }

    let url;
    try {
        url = new URL(text.trim());
    } catch {
        return null;
    }

    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        return null;
    }

    const host = url.hostname.replace(/^www\.|^m\./, '');
    const start = parseTimeToSeconds(url.searchParams.get('t') || url.searchParams.get('start') || '');

    if (host === 'youtu.be') {
        const id = url.pathname.slice(1).split('/')[0];
        return YOUTUBE_ID.test(id) ? { type: 'embed', src: youTubeEmbedUrl(id, start) } : null;
    }

    if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
        const watchId = url.searchParams.get('v');
        if (watchId && YOUTUBE_ID.test(watchId)) {
            return { type: 'embed', src: youTubeEmbedUrl(watchId, start) };
        }
        const pathMatch = /^\/(?:embed|shorts|live)\/([A-Za-z0-9_-]{11})(?:[/?#]|$)/.exec(url.pathname);
        if (pathMatch) {
            return { type: 'embed', src: youTubeEmbedUrl(pathMatch[1], start) };
        }
        return null;
    }

    if (host === 'rumble.com') {
        if (url.pathname.startsWith('/embed/')) {
            const normalized = `https://rumble.com${url.pathname}${url.search}`;
            return isAllowedEmbedSrc(normalized) ? { type: 'embed', src: normalized } : null;
        }
        if (/^\/v[a-z0-9]+(?:-|\.html$)/i.test(url.pathname)) {
            return { type: 'resolve', url: `https://rumble.com${url.pathname}` };
        }
        return null;
    }

    return null;
};

// Looks up a Rumble page link or embed link through the backend proxy
// (Rumble's oEmbed endpoint has no CORS headers, so the browser can't ask
// directly). Resolves to { embedUrl, title, thumbnailUrl } — title and
// thumbnailUrl are '' when Rumble reported none, and the thumbnail is
// https only. Throws with a user-facing message.
export const resolveEmbedUrl = async (rumbleUrl) => {
    const base = API_URL.replace(/\/+$/, '');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);

    let response;
    let payload = null;
    try {
        response = await fetch(
            `${base}/api/embed/resolve?url=${encodeURIComponent(rumbleUrl)}`,
            { signal: controller.signal }
        );
        try {
            payload = await response.json();
        } catch {
            payload = null;
        }
    } catch {
        throw new Error('Could not look up that video');
    } finally {
        clearTimeout(timer);
    }

    if (!response.ok) {
        throw new Error(payload?.message || 'Could not look up that video');
    }

    const embedUrl = payload?.embedUrl;
    if (!isAllowedEmbedSrc(embedUrl)) {
        throw new Error('Could not look up that video');
    }
    return {
        embedUrl,
        title: typeof payload.title === 'string' ? payload.title.trim().slice(0, 300) : '',
        thumbnailUrl: isHttpsUrl(payload.thumbnailUrl) ? payload.thumbnailUrl.trim() : '',
    };
};
